import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { Form } from "@/components/ui/form";
import { Diff } from "@/features/FormCard/Diff";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

import {
  AutocompleteAsyncField,
  AutocompleteLabelField,
  InputField,
  SelectFieldWithSection,
} from "@/features/FormCard/Fields";

import { useDynamicForm } from "@/features/Edit/dynamic-edit-form";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { ExternalLink } from "@/components/ExternalLink";
import { Label } from "@radix-ui/react-label";
import { dynamicFormConfig, dynamicFormFields } from "./edit-form-conf";
import { TechnicalInfoCardStatic } from "./technical_info_card";
import { fieldTranslations } from "./translations";
import { DynamicFields } from "@/features/FormCard/DynamicCard";
import { useMemo } from "react";

export function C5StaticDatumsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    optionsMap,
    handleDelete,
    selectFieldsConfig,
    isLoadingTranslations,
    options,
  } = useDynamicForm({
    id,
    client,
    table_name,
    formConfig: dynamicFormConfig,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "concentrated_asset") {
        return {
          text: value,
        };
      } else if (key === dynamicFormFields.c5_issuer_name) {
        return {
          text: fetchedData[dynamicFormFields.c5_issuer_name],
          id: fetchedData.issuer_id,
        };
      }
      return value;
    },
  });

  const currencyOptions = useCurrencyOptions();

  const name = form.watch(dynamicFormFields.name);
  const assetClass1 = form.watch("asset_class_1");
  const assetClass2 = form.watch("asset_class_2");

  const translatedDynamicConfig = useMemo(() => {
    const cfg = selectFieldsConfig?.asset_class_2;
    if (!cfg) return undefined;

    const translatedSections = Object.fromEntries(
      Object.entries(cfg.section).map(([sectionName, fields]) => [
        sectionName,
        Object.fromEntries(
          Object.entries(fields).map(([fieldName, fieldConfig]) => [
            fieldName,
            {
              ...fieldConfig,
              label: t(`${table_name}.columns.${fieldName}`, {
                ns: "tables",
                defaultValue: fieldConfig.label,
              }),
            },
          ]),
        ),
      ]),
    );

    return { ...cfg, section: translatedSections };
  }, [selectFieldsConfig?.asset_class_2, t, table_name]);

  return (
    <>
      <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
        <div className="flex justify-center w-full">
          <Card className="p-4 bg-white w-full ">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <CardHeader className="font-bold text-gray-700">
                  {t(`${table_name}.title`, {
                    ns: "tables",
                    defaultValue: "Actif",
                  })}{" "}
                  {id}
                </CardHeader>
                <Divider />
                <CardBody className="flex flex-col gap-4">
                  <Label>
                    {t(`${table_name}.sections.general_information`, {
                      ns: "tables",
                      defaultValue: "Informations générales",
                    })}
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-8">
                      <InputField
                        name={dynamicFormFields.name}
                        label={t(`${table_name}.columns.name`, {
                          ns: "tables",
                          defaultValue: "Nom",
                        })}
                        isRequired={true}
                        capitalize={true}
                        control={form.control}
                      />
                    </div>
                    <div className="md:col-span-4">
                      <AutocompleteLabelField
                        name={dynamicFormFields.currency}
                        label={t(`${table_name}.columns.currency`, {
                          ns: "tables",
                          defaultValue: "Devise",
                        })}
                        options={currencyOptions}
                        isRequired={true}
                        control={form.control}
                        className="min-w-0"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                    <InputField
                      name={dynamicFormFields.isin}
                      label={t(`${table_name}.columns.isin`, {
                        ns: "tables",
                        defaultValue: "ISIN",
                      })}
                      control={form.control}
                    />
                    <InputField
                      name={dynamicFormFields.code}
                      label={t(`${table_name}.columns.code`, {
                        ns: "tables",
                        defaultValue: "Code",
                      })}
                      control={form.control}
                    />
                    <InputField
                      name={dynamicFormFields.ticker}
                      label={t(`${table_name}.columns.ticker`, {
                        ns: "tables",
                        defaultValue: "Ticker",
                      })}
                      control={form.control}
                    />
                  </div>
                  <AutocompleteAsyncField
                    name={dynamicFormFields.c5_issuer_name}
                    label={t(`${table_name}.columns.c5_issuer_name`, {
                      ns: "tables",
                      defaultValue: "Emetteur",
                    })}
                    control={form.control}
                    base_url={`${Clients.C5}/${Tables.C5_ISSUERS}`}
                    id_column="issuer_id"
                    column_name={dynamicFormFields.name}
                  />
                  <SelectFieldWithSection
                    listboxWrapperClassNames="!h-[400px] !max-h-none"
                    primary_key={"asset_class_1"}
                    name={dynamicFormFields.asset_class_2}
                    label={t(`${table_name}.columns.asset_class_2`, {
                      ns: "tables",
                      defaultValue: "Classe d'actifs",
                    })}
                    control={form.control}
                    sections={optionsMap[dynamicFormFields.asset_class_2]}
                    placeholder={t("form.placeholders.select")}
                    isRequired
                  />
                  <DynamicFields
                    primaryValue={assetClass1}
                    fieldValue={assetClass2}
                    control={form.control}
                    config={translatedDynamicConfig}
                    isLoadingTranslations={isLoadingTranslations}
                    options={options}
                  />
                </CardBody>
                <CardFooter>
                  <FormActionsUpdate
                    onReset={handleReset}
                    onDelete={openDeleteModal}
                    isDirty={form.formState.isDirty}
                    elementId={id}
                    client={client}
                    allowDelete={false}
                  />
                </CardFooter>
              </form>
            </Form>
          </Card>
          {name && (
            <div className="absolute top-[3.20rem] right-12 2xl:right-10 lg:flex gap-2 flex-row hidden">
              <Button
                href={toWorkspacePath(
                  `/admin/${Clients.C5}/${Tables.C5_TRANSACTIONS}`,
                )}
                queryParams={{ c5_static_data_id_equal: id }}
                variant="bordered"
                color="primary"
                showAnchorIcon
                as={ExternalLink}
                radius="full"
                className="bg-white border-1 border-neutral-300 shadow-xl"
              >
                {t("actions.transactions")}
              </Button>
              <Button
                href={toWorkspacePath(
                  `/admin/${Clients.C5}/${Tables.C5_TIME_SERIES}`,
                )}
                queryParams={{ c5_static_data_id_equal: id }}
                variant="bordered"
                color="primary"
                showAnchorIcon
                as={ExternalLink}
                radius="full"
                className="bg-white border-1 border-neutral-300 shadow-xl"
              >
                {t("actions.valuations")}
              </Button>
              {form.watch(dynamicFormFields.c5_issuer_name)?.id && (
                <Button
                  href={toWorkspacePath(
                    `/admin/${Clients.C5}/${Tables.C5_ISSUERS}/${form.watch(dynamicFormFields.c5_issuer_name)?.id}`,
                  )}
                  variant="bordered"
                  color="primary"
                  showAnchorIcon
                  as={ExternalLink}
                  radius="full"
                  className="bg-white border-1 border-neutral-300 shadow-xl"
                >
                  {t("entities.issuer")}
                </Button>
              )}
            </div>
          )}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
            children={
              changedFields
                ? Diff(
                    changedFields,
                    ["issuer_id"],
                    fieldTranslations,
                    table_name,
                  )
                : t("no_changes")
            }
          />

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </div>
      </div>
      <TechnicalInfoCardStatic
        id={Number(id)}
        table_name={table_name}
        strategy_id_control={form.control}
        client={client}
      />
    </>
  );
}
